import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { Row, Col } from 'reactstrap';

import { callApi, formatAmount, formatDateTime, DISCOS } from '../../utils';
import { showError } from '../../actions/notificationActions';

import DataTable, { PageHeader, EmptyState } from '../DataTable';
import FilterBuilder, { buildWhere } from '../FilterBuilder';
import FilterPanel, { whereToConditions } from '../SavedFilters/FilterPanel';
import { createSavedFilter } from '../../services/savedFiltersApi';
import UserLink from '../Users/UserLink';
import { openTransactionModal } from '../../actions/modalActions';

const STATUS_OPTIONS = [
  'NEW',
  'SUCCESSFUL',
  'PROCESSING',
  'FAILED',
  'CANCELED',
];
const CHANNEL_OPTIONS = ['WEB', 'MOBILE', 'USSD', 'API', 'AGENT', 'POS'];

const ORDER_FIELDS = [
  { key: 'order_id', label: 'Order ID', type: 'string' },
  { key: 'meter_no', label: 'Meter No', type: 'string' },
  { key: 'phone', label: 'Phone', type: 'string' },
  { key: 'name', label: 'Customer Name', type: 'string' },
  {
    key: 'disco_code',
    label: 'Disco',
    type: 'enum',
    options: DISCOS.map((d) => d.name),
  },
  { key: 'channel', label: 'Channel', type: 'enum', options: CHANNEL_OPTIONS },
  { key: 'api_user_id', label: 'Business ID', type: 'number' },
  {
    key: 'order_status',
    label: 'Status',
    type: 'enum',
    options: STATUS_OPTIONS,
  },
  { key: 'created_at', label: 'Date', type: 'date' },
];

function encodeConditions(conditions) {
  return encodeURIComponent(
    JSON.stringify(conditions.map(({ id, ...rest }) => rest))
  );
}

function parseUrlParams(search) {
  const params = new URLSearchParams(search);
  let conditions = [];
  let logicalOp = '$and';
  let page = 1;

  const raw = params.get('c');
  if (raw) {
    try {
      const parsed = JSON.parse(decodeURIComponent(raw));
      conditions = parsed.map((c, i) => ({ ...c, id: Date.now() + i }));
    } catch (_) {}
  }

  const op = params.get('op');
  if (op === '$and' || op === '$or') logicalOp = op;

  const p = parseInt(params.get('page'), 10);
  if (p > 0) page = p;

  return { conditions, logicalOp, page };
}

class List extends Component {
  constructor(props) {
    super(props);
    const { conditions, logicalOp, page } = parseUrlParams(
      props.location ? props.location.search : ''
    );
    this.state = {
      rows: [],
      conditions,
      logicalOp,
      page,
      limit: 30,
      loading: false,
      pin: false,
      meta: { pages: 1, checkpoint: null },
      activeSavedFilter: null,
      builderKey: 0,
    };
  }

  componentDidMount() {
    if (this.state.conditions.length > 0) {
      this.load();
    }
  }

  startLoad = () => this.setState({ loading: true });
  stopLoad = () => this.setState({ loading: false });
  scrollToTop = () => setTimeout(() => window.scrollTo(0, 0), 200);

  syncUrl = (conditions, logicalOp, page) => {
    const params = new URLSearchParams();
    if (conditions.length > 0) {
      params.set('c', encodeConditions(conditions));
      params.set('op', logicalOp);
    }
    if (page > 1) params.set('page', String(page));
    const search = params.toString();
    this.props.history.replace({ search: search ? `?${search}` : '' });
  };

  load = () => {
    const { conditions, logicalOp, page, meta, pin } = this.state;
    const where = buildWhere(conditions, logicalOp);
    const hasFilter = Object.keys(where).length > 0;

    this.startLoad();
    callApi('/orders', {
      include: ['agent', 'merchant', 'customer'],
      page,
      checkpoint: pin && meta && meta.checkpoint ? meta.checkpoint : null,
      ...(hasFilter ? { where } : {}),
    })
      .then(({ data = [], meta = {} }) => {
        this.setState({ loading: false, rows: data, meta });
        this.scrollToTop();
      })
      .catch((err) => {
        this.stopLoad();
        this.props.dispatch(showError(err));
      });
  };

  setPage = (page) => {
    if (page) {
      this.setState({ page }, () => {
        this.syncUrl(this.state.conditions, this.state.logicalOp, page);
        this.load();
      });
    }
  };

  clearFilters = () => {
    this.setState(
      {
        conditions: [],
        logicalOp: '$and',
        page: 1,
        activeSavedFilter: null,
        rows: [],
      },
      () => this.syncUrl([], '$and', 1)
    );
  };

  togglePin = () => {
    this.setState(
      ({ pin }) => ({ pin: !pin, page: 1 }),
      () => {
        if (!this.state.pin) this.load();
      }
    );
  };

  openTransaction = (t) => this.props.dispatch(openTransactionModal(t));

  handleFilterChange = (_where, conditions, logicalOp) => {
    this.setState(
      { conditions, logicalOp, page: 1, activeSavedFilter: null },
      () => {
        this.syncUrl(conditions, logicalOp, 1);
        if (conditions.length > 0) {
          this.load();
        } else {
          this.setState({ rows: [] });
        }
      }
    );
  };

  handleSaveFilter = ({ label, description }) => {
    const { conditions, logicalOp } = this.state;
    const where = buildWhere(conditions, logicalOp);
    if (Object.keys(where).length === 0) return;
    createSavedFilter('orders', {
      type: 'custom',
      label,
      description,
      value: JSON.stringify(where),
    })
      .then(({ data }) => {
        this.setState({ activeSavedFilter: data });
      })
      .catch((err) => {
        this.props.dispatch(showError(err));
      });
  };

  handleApplySavedFilter = (filter) => {
    const parsed = whereToConditions(filter.value, ORDER_FIELDS);
    const conditions = parsed.conditions.map((c, i) => ({
      ...c,
      id: Date.now() + i,
    }));
    const logicalOp = parsed.logicalOp;
    this.setState(
      function (prev) {
        return {
          conditions,
          logicalOp,
          activeSavedFilter: filter,
          page: 1,
          builderKey: prev.builderKey + 1,
        };
      },
      () => {
        this.syncUrl(conditions, logicalOp, 1);
        if (conditions.length > 0) this.load();
      }
    );
  };

  handleSavedFilterUpdated = (filter) => {
    this.setState(function (prev) {
      var active = prev.activeSavedFilter;
      return {
        activeSavedFilter: active && active.id === filter.id ? filter : active,
      };
    });
  };

  handleDeletedSavedFilter = (filterId) => {
    this.setState(function (prev) {
      var active = prev.activeSavedFilter;
      return {
        activeSavedFilter: active && active.id === filterId ? null : active,
      };
    });
  };

  render() {
    const {
      rows,
      page,
      loading,
      limit,
      meta,
      pin,
      conditions,
      logicalOp,
      activeSavedFilter,
      builderKey,
    } = this.state;

    const hasFilter = conditions.length > 0;
    const hasResults = rows.length > 0;

    return (
      <div className="container-fluid">
        <Row className="mt-3 mt-md-4">
          <Col xs={12}>
            {/* ── Page header ───────────────────────────────────────────────── */}
            <PageHeader
              title="ORDERS"
              subtitle={
                (hasFilter
                  ? 'Filtered results — page ' + page
                  : 'Page ' + page) +
                (pin && meta.checkpoint
                  ? ' ⏸ ' + formatDateTime(meta.checkpoint)
                  : '')
              }
            >
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={this.load}
                disabled={loading}
                title="Refresh"
              >
                <i
                  className={
                    'now-ui-icons loader_refresh' + (loading ? ' spinner' : '')
                  }
                />
              </button>
              <button
                className={
                  'btn btn-sm ' +
                  (pin ? 'btn-warning' : 'btn-outline-secondary')
                }
                onClick={this.togglePin}
                style={{ whiteSpace: 'nowrap' }}
              >
                <i
                  className={
                    'now-ui-icons ' +
                    (pin ? 'media-1_button-play' : 'media-1_button-pause')
                  }
                  style={{ marginRight: 4 }}
                />
                {pin ? 'Resume' : 'Pause'}
              </button>
            </PageHeader>

            {/* ── Filter builder ─────────────────────────────────────────────── */}
            <div style={{ marginBottom: 16 }}>
              <FilterBuilder
                key={builderKey}
                fields={ORDER_FIELDS}
                initialConditions={conditions}
                initialLogicalOp={logicalOp}
                onChange={this.handleFilterChange}
                onSaveFilter={this.handleSaveFilter}
              />
            </div>

            {/* ── No filter: SavedFilters panel as placeholder ───────────────── */}
            {!hasFilter && (
              <FilterPanel
                inline
                resource="orders"
                fields={ORDER_FIELDS}
                activeSavedFilter={activeSavedFilter}
                onApplySavedFilter={this.handleApplySavedFilter}
                onSavedFilterUpdated={this.handleSavedFilterUpdated}
                onDeletedSavedFilter={this.handleDeletedSavedFilter}
              />
            )}

            {/* ── Filter applied: results table ──────────────────────────────── */}
            {hasFilter && (
              <DataTable
                minWidth={700}
                loading={loading}
                page={page}
                total={meta.pages}
                onPageChange={this.setPage}
                showPagination={hasResults}
              >
                <thead className="text-primary">
                  <tr>
                    <th>#</th>
                    <th>Order ID</th>
                    <th>Meter No</th>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Disco</th>
                    <th>Channel</th>
                    <th>Business</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>

                {!loading && !hasResults ? (
                  <EmptyState
                    colSpan={10}
                    heading="No orders match your filters"
                    message="Try adjusting or removing one of the conditions above."
                    actionLabel="Clear filters"
                    onAction={this.clearFilters}
                  />
                ) : (
                  <tbody>
                    {rows.map((t, i) => (
                      <tr
                        key={t.id}
                        onClick={() => this.openTransaction(t)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{loading ? '—' : i + 1 + (page - 1) * limit}</td>
                        <td>{t.order_id}</td>
                        <td>{t.meter_no}</td>
                        {/* The label stays the name captured on the order
                            — that is what this column has always meant.
                            The hover target is the account behind it: the
                            included relation when there is one, and the
                            order's own phone (which `/user` resolves just
                            as well) for guest and API orders that never
                            had one. */}
                        <td>
                          <UserLink
                            user={
                              t.customer || { name: t.name, phone: t.phone }
                            }
                          >
                            {t.name || t.phone || 'N/A'}
                          </UserLink>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {formatAmount(t.amount)}
                        </td>
                        <td>{t.disco_code}</td>
                        <td>{t.channel}</td>
                        <td>{t.merchant && t.merchant.name}</td>
                        <td>{t.order_status}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {formatDateTime(t.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </DataTable>
            )}
          </Col>
        </Row>
      </div>
    );
  }
}

export default withRouter(connect()(List));
